"use client";

import Link from "next/link";
import { ReactNode, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

export const DASHBOARD_TABS = [
  { label: "Fees", href: "/dashboard" },
  { label: "Whitelabel", href: "/dashboard/whitelabel" },
  { label: "Banners", href: "/dashboard/banners" },
  { label: "Tokens", href: "/dashboard/tokens" },
  { label: "Promo Codes", href: "/dashboard/promo-codes" },
  { label: "Affiliates", href: "/dashboard/affiliates" },
  { label: "Swaps", href: "/dashboard/swaps" },
  { label: "Burns", href: "/dashboard/burns" },
  { label: "Revenue", href: "/dashboard/swaps/by-token" },
  { label: "Spaces", href: "/dashboard/spaces" },
];

export const CHAIN_OPTIONS = [
  { id: 1, name: "Ethereum" },
  { id: 56, name: "BNB Chain" },
  { id: 137, name: "Polygon" },
  { id: 42161, name: "Arbitrum" },
  { id: 10, name: "Optimism" },
  { id: 43114, name: "Avalanche" },
  { id: 8453, name: "Base" },
  { id: 1151111081099710, name: "Solana" },
  { id: 59144, name: "Linea" },
  { id: 534352, name: "Scroll" },
  { id: 81457, name: "Blast" },
  { id: 5000, name: "Mantle" },
  { id: 100, name: "Gnosis" },
  { id: 250, name: "Fantom" },
] as const;

export const CHAIN_NAMES: Record<number, string> = Object.fromEntries(
  CHAIN_OPTIONS.map((chain) => [chain.id, chain.name]),
);

export const usd = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);

export const cardStyle = {
  background: "#1a1a1a",
  border: "1px solid #222",
  borderRadius: 12,
} as const;

export const inputStyle = {
  width: "100%",
  background: "#111",
  border: "1px solid #333",
  color: "#fff",
  padding: "10px 12px",
  borderRadius: 8,
  fontSize: 14,
  boxSizing: "border-box" as const,
};

export const buttonStyle = {
  background: "#f9cd4f",
  color: "#111",
  border: "none",
  padding: "10px 16px",
  borderRadius: 8,
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 14,
} as const;

export function DashboardShell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: "#111",
        minHeight: "100vh",
        color: "#fff",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

// Admin info component that fetches and displays current admin
function AdminInfo() {
  const router = useRouter();
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    async function fetchAdmin() {
      try {
        const res = await fetch("/api/auth/admin-me");
        if (res.ok) {
          const data = await res.json();
          setUsername(data.username);
        } else {
          // Not authenticated - redirect to login
          router.replace("/dashboard/login");
        }
      } catch {
        router.replace("/dashboard/login");
      } finally {
        setLoading(false);
      }
    }
    fetchAdmin();
  }, [router]);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await fetch("/api/auth/admin-logout", { method: "POST" });
      router.replace("/dashboard/login");
    } catch {
      // Still redirect on error
      router.replace("/dashboard/login");
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ color: "#888", fontSize: 13 }}>Loading...</span>
      </div>
    );
  }

  if (!username) {
    return null;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ color: "#888", fontSize: 13 }}>
        Logged in as <span style={{ color: "#fff", fontWeight: 600 }}>{username}</span>
      </span>
      <button
        onClick={handleLogout}
        disabled={loggingOut}
        style={{
          background: "#333",
          color: "#fff",
          border: "none",
          padding: "6px 12px",
          borderRadius: 6,
          fontSize: 12,
          cursor: loggingOut ? "not-allowed" : "pointer",
          opacity: loggingOut ? 0.6 : 1,
        }}
      >
        {loggingOut ? "..." : "Logout"}
      </button>
    </div>
  );
}

export function DashboardHeader({
  activeTab,
  title = "ForgeSwap Admin Dashboard",
  description = "Manage analytics, submissions, banners, and featured tokens.",
  controls,
}: {
  activeTab: string;
  title?: string;
  description?: string;
  controls?: ReactNode;
}) {
  return (
    <div
      style={{
        borderBottom: "1px solid #222",
        padding: "20px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 24,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "#f9cd4f", margin: 0 }}>
            {title}
          </h1>
          <AdminInfo />
        </div>
        <p style={{ color: "#888", fontSize: 14, margin: "6px 0 0" }}>{description}</p>
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
          {DASHBOARD_TABS.map((tab) => {
            const isActive = tab.href === activeTab;
            return (
              <Link
                key={tab.href}
                href={tab.href}
                style={{
                  color: isActive ? "#f9cd4f" : "#888",
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  borderBottom: `2px solid ${isActive ? "#f9cd4f" : "transparent"}`,
                  paddingBottom: 4,
                }}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>
      {controls ? (
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {controls}
        </div>
      ) : null}
    </div>
  );
}
